import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import {
  SkyPopoverAlignment,
  SkyPopoverComponent,
  SkyPopoverTrigger
} from '../popover';

import {
  SkyResources
} from '../resources';

import {
  SkyWindowRefService
} from '../window';

import {
  SkyDropdownMessage,
  SkyDropdownMessageType,
  SkyDropdownTriggerType
} from './types';


export class SkyDropdownComponent implements OnInit, OnDestroy {
  
  public alignment: SkyPopoverAlignment = 'left';

  
  public get buttonStyle(): string{
    return this._buttonStyle || 'default';
  }

  public set buttonStyle(value: string) {
    this._buttonStyle = value;
  }

  
  public set buttonType(value: string) {
    this._buttonType = value;
  }

  public get buttonType(): string {
    return this._buttonType || 'select';
  }

  
  public get label(): string{
    return this._label || SkyResources.getString('context_menu_default_label');
  }

  public set label(value: string) {
    this._label = value;
  }

  
  public dismissOnBlur = true;

  
  public messageStream = new Subject<SkyDropdownMessage>();

  
  public title: string;

  
  public set trigger(value: SkyDropdownTriggerType) {
    this._trigger = value;
  }

  public get trigger(): SkyDropdownTriggerType {
    return this._trigger || 'click';
  }

  
  private triggerButton: ElementRef;

  
  private popover: SkyPopoverComponent;

  private ngUnsubscribe = new Subject();
  private isKeyboardActive = false;
  private isOpen = false;

  private _buttonType: string;
  private _buttonStyle: string;
  private _label: string;
  private _trigger: SkyDropdownTriggerType;

  constructor(
    private windowRef: SkyWindowRefService
  ) { }

  public ngOnInit() {
    this.messageStream
      .takeUntil(this.ngUnsubscribe)
      .subscribe((message: SkyDropdownMessage) => {
        this.handleIncomingMessages(message);
      });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  
  public onKeyDown(event: KeyboardEvent) {
    const key = event.key.toLowerCase();

    if (this.isOpen) {
      /* tslint:disable-next-line:switch-default */
      switch (key) {
        // After an item is selected with the enter key,
        // wait a moment before returning focus to the dropdown trigger element.
        case 'enter':
        this.windowRef.getWindow().setTimeout(() => {
          this.sendMessage(SkyDropdownMessageType.FocusTriggerButton);
        });
        break;

        // Allow the menu to be opened with the arrowdown key
        // if it is first opened with the mouse.
        case 'down':
        case 'arrowdown':
        if (!this.isKeyboardActive) {
          this.isKeyboardActive = true;
          this.sendMessage(SkyDropdownMessageType.FocusFirstItem);
          event.preventDefault();
        }
        break;
      }

      return;
    }

    /* tslint:disable-next-line:switch-default */
    switch (key) {
      case 'enter':
      this.isKeyboardActive = true;
      break;

      case 'down':
      case 'arrowdown':
      this.isKeyboardActive = true;
      this.sendMessage(SkyDropdownMessageType.Open);
      event.preventDefault();
      break;
    }
  }

  public onPopoverOpened() {
    this.isOpen = true;
    // Focus the first item if the menu was opened with the keyboard.
    if (this.isKeyboardActive) {
      this.sendMessage(SkyDropdownMessageType.FocusFirstItem);
    }
  }

  public onPopoverClosed() {
    this.isOpen = false;
    this.isKeyboardActive = false;
  }

  public getPopoverTriggerType(): SkyPopoverTrigger {
    // Map the dropdown trigger type to the popover trigger type.
    return (this.trigger === 'click') ? 'click' : 'mouseenter';
  }

  private handleIncomingMessages(message: SkyDropdownMessage) {
    /* tslint:disable-next-line:switch-default */
    switch (message.type) {
      case SkyDropdownMessageType.Open:
      this.positionPopover();
      break;

      case SkyDropdownMessageType.Close:
      this.popover.close();
      break;

      case SkyDropdownMessageType.Reposition:
      // Only reposition the dropdown if it is already open.
      if (this.isOpen) {
        this.windowRef.getWindow().setTimeout(() => {
          this.popover.reposition();
        });
      }
      break;

      case SkyDropdownMessageType.FocusTriggerButton:
      this.triggerButton.nativeElement.focus();
      break;
    }
  }

  private sendMessage(type: SkyDropdownMessageType) {
    this.messageStream.next({ type });
  }

  private positionPopover() {
    this.popover.positionNextTo(
      this.triggerButton,
      'below',
      this.alignment
    );
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-dropdown',
  template: `<div class="sky-dropdown">
  <button
    type="button"
    class="sky-btn sky-dropdown-button"
    [ngClass]="[
      'sky-dropdown-button-type-' + buttonType,
      'sky-btn-' + buttonStyle
    ]"
    [attr.aria-label]="label"
    [attr.title]="title"
    [skyPopover]="popover"
    [skyPopoverTrigger]="getPopoverTriggerType()"
    [skyPopoverAlignment]="alignment"
    skyPopoverPlacement="below"
    #triggerButton>

    <div [ngSwitch]="buttonType">
      <ng-template ngSwitchCase="context-menu">
        <i class="fa fa-ellipsis-h"></i>
      </ng-template>

      <ng-template ngSwitchDefault>
        <div
          class="sky-dropdown-button-container"
          *ngIf="buttonType === 'select' || buttonType === 'tab' || !buttonType">
          <div class="sky-dropdown-button-content-container">
            <ng-content
              select="sky-dropdown-button">
            </ng-content>
          </div>
          <div class="sky-dropdown-button-icon-container">
            <i class="fa fa-caret-down sky-dropdown-caret"></i>
          </div>
        </div>
        <div
          *ngIf="buttonType !== 'select' && buttonType !== 'tab' && buttonType">
          <i
            class="fa fa-lg"
            [ngClass]="['fa-' + buttonType]"></i>
        </div>
      </ng-template>
    </div>
  </button>
  <sky-popover
    placement="below"
    [alignment]="alignment"
    [dismissOnBlur]="dismissOnBlur"
    (popoverOpened)="onPopoverOpened()"
    (popoverClosed)="onPopoverClosed()"
    [@.disabled]="true"
    #popover>
    <ng-content
      select="sky-dropdown-menu">
    </ng-content>
  </sky-popover>
</div>
`,
  styles: [`.sky-dropdown ::ng-deep .sky-popover-container {
  padding: 0;
  min-width: auto;
  max-width: none;
}

.sky-dropdown ::ng-deep .sky-popover-container .sky-popover {
  border-radius: 0 !important;
  border: 0 !important;
}

.sky-dropdown ::ng-deep .sky-popover-container .sky-popover-body {
  padding: 0;
}

.sky-dropdown ::ng-deep .sky-popover-container .sky-popover-arrow {
  display: none;
}

.sky-dropdown-button-type-tab {
  background-color: transparent;
  border: none;
  border-radius: 4px 4px 0 0;
  color: #686c73;
  cursor: pointer;
  display: inline-block;
  font-weight: 600;
  line-height: 1.8;
  padding: 8px 16px;
  background-color: #007ca6;
  color: #fff;
  max-width: 100%;
}

.sky-dropdown-button-type-tab:hover {
  background-color: #eeeeef;
  color: #282b31;
  text-decoration: none;
}

.sky-dropdown-button-type-tab:hover {
  background-color: #007ca6;
  color: #fff;
}

.sky-dropdown-button-type-context-menu {
  border-radius: 50%;
  padding-bottom: 3px;
  padding-left: 8px;
  padding-right: 8px;
  padding-top: 3px;
}

.sky-dropdown-caret {
  margin-left: 10px;
}

.sky-dropdown-button-container {
  display: flex;
}

.sky-dropdown-button-content-container {
  flex-shrink: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sky-dropdown-button-icon-container {
  flex-grow: 1;
}
`],
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: SkyWindowRefService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'alignment': [{ type: Input },],
'buttonStyle': [{ type: Input },],
'buttonType': [{ type: Input },],
'label': [{ type: Input },],
'dismissOnBlur': [{ type: Input },],
'messageStream': [{ type: Input },],
'title': [{ type: Input },],
'trigger': [{ type: Input },],
'triggerButton': [{ type: ViewChild, args: ['triggerButton', ] },],
'popover': [{ type: ViewChild, args: [SkyPopoverComponent, ] },],
'onKeyDown': [{ type: HostListener, args: ['keydown', ['$event'], ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
